import { useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAddKnowledgeBaseDocument,
  useRemoveKnowledgeBaseDocument,
} from '../../hooks/useKnowledge';
import type { KnowledgeBase } from '../../types';

/**
 * A document's knowledge-base memberships, managed from the document (task 574).
 *
 * Bases are groupings, so membership is edited wherever scope is being decided —
 * here on the library row, and from the base's own detail page. Removing a chip
 * removes the membership only: the document, its text and its indexed chunks are
 * untouched, which is why this needs no confirmation the way deleting does.
 *
 * A document in no base is deliberately invisible to agent search — it can still
 * be handed to a single run as that run's subject document.
 */
interface DocumentKnowledgeBasesProps {
  documentId: string;
  /** Bases this document currently belongs to. */
  memberships: KnowledgeBase[];
  /** Every base in the account, for the add menu. */
  allBases: KnowledgeBase[];
}

export function DocumentKnowledgeBases({
  documentId,
  memberships,
  allBases,
}: DocumentKnowledgeBasesProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const addMembership = useAddKnowledgeBaseDocument();
  const removeMembership = useRemoveKnowledgeBaseDocument();

  const memberIds = new Set(memberships.map((base) => base.id));
  const available = allBases.filter((base) => !memberIds.has(base.id));

  const handleAdd = async (knowledgeBaseId: string) => {
    setIsPickerOpen(false);
    try {
      await addMembership.mutateAsync({ knowledgeBaseId, documentId });
    } catch (error) {
      console.error('Failed to add the document to the knowledge base:', error);
    }
  };

  const handleRemove = async (knowledgeBaseId: string) => {
    try {
      await removeMembership.mutateAsync({ knowledgeBaseId, documentId });
    } catch (error) {
      console.error('Failed to remove the document from the knowledge base:', error);
    }
  };

  return (
    <div className="relative" data-testid={`document-bases-${documentId}`}>
      <div className="flex flex-wrap items-center gap-1">
        {memberships.map((base) => (
          <span
            key={base.id}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-200"
          >
            {base.name}
            <button
              type="button"
              onClick={() => handleRemove(base.id)}
              disabled={removeMembership.isPending}
              aria-label={`Remove from ${base.name}`}
              className="text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-100 disabled:opacity-50"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}

        {memberships.length === 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">Not in any base</span>
        )}

        {available.length > 0 && (
          <button
            type="button"
            onClick={() => setIsPickerOpen((open) => !open)}
            disabled={addMembership.isPending}
            aria-label="Add to a knowledge base"
            aria-expanded={isPickerOpen}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-gray-300 dark:border-gray-600 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 disabled:opacity-50"
          >
            <PlusIcon className="h-3 w-3" />
            Add
          </button>
        )}
      </div>

      {isPickerOpen && (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 w-56 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
        >
          {available.map((base) => (
            <button
              key={base.id}
              type="button"
              role="menuitem"
              onClick={() => handleAdd(base.id)}
              className="block w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {base.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default DocumentKnowledgeBases;
